import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Download, Eye, Filter, Archive, ArchiveRestore, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { CatEventSelect } from '@/components/CatEventSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  'Open': 'bg-accent/20 text-accent',
  'Pending Docs': 'bg-warning-bg text-warning-text',
  'Negotiation': 'bg-blue-100 text-blue-700',
  'Settled': 'bg-success-bg text-success-text',
  'Paid in Full': 'bg-green-100 text-green-800',
  'Closed': 'bg-gray-200 text-gray-700'
};

// Unique colors for each user/adjuster
const USER_COLORS = [
  { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300' },
  { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
  { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' },
  { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300' },
  { bg: 'bg-pink-100', text: 'text-pink-700', border: 'border-pink-300' },
  { bg: 'bg-teal-100', text: 'text-teal-700', border: 'border-teal-300' },
  { bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-300' },
  { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300' },
  { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300' },
  { bg: 'bg-cyan-100', text: 'text-cyan-700', border: 'border-cyan-300' },
];

// Function to get consistent color for a user
const getUserColor = (userId, userColorMap) => {
  if (!userId || !userColorMap[userId]) {
    return { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-300' };
  }
  return userColorMap[userId];
};

// Firm badge colors
const FIRM_COLORS = {
  'Apicella': 'bg-blue-100 text-blue-700',
  'MHA': 'bg-purple-100 text-purple-700'
};

const getFirmBadgeClass = (firm) => FIRM_COLORS[firm] || 'bg-gray-100 text-gray-600';

export const ClaimsPage = () => {
  const { hasRole, user } = useAuth();
  const navigate = useNavigate();
  const [claims, setClaims] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [catEvents, setCatEvents] = useState([]);
  const [catEventFilter, setCatEventFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [adjusterFilter, setAdjusterFilter] = useState('all');
  const [firmFilter, setFirmFilter] = useState('all');
  const [sortBy, setSortBy] = useState(null); // 'firm-asc', 'firm-desc'
  const [showArchived, setShowArchived] = useState(false);
  const [userColorMap, setUserColorMap] = useState({});
  const [selectedClaims, setSelectedClaims] = useState([]);
  const [isReserveReportDialogOpen, setIsReserveReportDialogOpen] = useState(false);
  const [selectedFields, setSelectedFields] = useState([
    'customer_name', 'date_of_loss', 'date_of_assignment', 
    'our_estimate_value', 'expected_loss_settlement', 'fee_percentage', 'town', 'state'
  ]);
  
  // Available fields for Reserve Report
  const RESERVE_REPORT_FIELDS = [
    { key: 'customer_name', label: 'Customer Name' },
    { key: 'date_of_loss', label: 'Date of Loss' },
    { key: 'date_of_assignment', label: 'Date of Assignment' },
    { key: 'our_estimate_value', label: 'Estimate Value' },
    { key: 'expected_loss_settlement', label: 'Expected Settlement' },
    { key: 'fee_percentage', label: 'Fee %' },
    { key: 'town', label: 'Town' },
    { key: 'state', label: 'State' },
    { key: 'policy_number', label: 'Policy Number' },
    { key: 'claim_number', label: 'Claim Number' },
    { key: 'actual_loss_settlement', label: 'Actual Settlement' },
    { key: 'estimate_fee', label: 'Estimate Fee' },
    { key: 'status', label: 'Status' },
    { key: 'file_name', label: 'File Name' },
  ];
  const [formData, setFormData] = useState({
    customer_id: '',
    customer_name: '',
    assigned_to: '',
    firm: 'Apicella',
    file_name: '',
    town: '',
    state: '',
    date_of_loss: '',
    date_of_assignment: '',
    policy_number: '',
    claim_number: '',
    our_estimate_value: '',
    expected_loss_settlement: '',
    estimate_fee: '',
    fee_percentage: '',
    actual_loss_settlement: '',
    offer_received: false,
    status: 'Open',
    internal_comments: '',
    notes: '',
    cat_event_id: ''
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build query params
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (catEventFilter !== 'all') params.append('cat_event_id', catEventFilter);
      params.append('archived', showArchived.toString());
      const queryString = params.toString() ? `?${params.toString()}` : '';
      
      const [claimsRes, customersRes, usersRes, catEventsRes] = await Promise.all([
        axios.get(`${API}/claims${queryString}`),
        axios.get(`${API}/customers`),
        axios.get(`${API}/users/list`),
        axios.get(`${API}/cat-events`)
      ]);
      setCatEvents(catEventsRes.data);

      // Create a user lookup map and color map
      const userMap = {};
      const colorMap = {};
      usersRes.data.forEach((user, index) => {
        userMap[user.user_id] = user.name;
        colorMap[user.user_id] = USER_COLORS[index % USER_COLORS.length];
      });
      setUserColorMap(colorMap);

      // Enrich claims with customer data and assigned user name
      const enrichedClaims = claimsRes.data.map((claim) => {
        // Use stored customer_name, or look up from customer if not stored
        let displayName = claim.customer_name || '';
        if (!displayName && claim.customer_id) {
          const customer = customersRes.data.find(c => c.customer_id === claim.customer_id);
          displayName = customer?.name || '';
        }
        if (!displayName) {
          displayName = 'No Customer';
        }
        
        return {
          ...claim,
          customer_name: displayName,
          carrier: claim.customer_id 
            ? (customersRes.data.find(c => c.customer_id === claim.customer_id)?.insurance_company || 'N/A')
            : 'N/A',
          assigned_to_name: userMap[claim.assigned_to] || 'Unassigned'
        };
      });

      setClaims(enrichedClaims);
      setCustomers(customersRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Failed to load claims');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, showArchived, catEventFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleArchive = async (claimId) => {
    try {
      await axios.post(`${API}/claims/${claimId}/archive`, {});
      toast.success('Claim archived successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive claim');
    }
  };

  const handleUnarchive = async (claimId) => {
    try {
      await axios.post(`${API}/claims/${claimId}/unarchive`, {});
      toast.success('Claim restored successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore claim');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        customer_id: formData.customer_id === 'none' || !formData.customer_id ? null : formData.customer_id,
        customer_name: formData.customer_name || '',
        assigned_to: formData.assigned_to,
        firm: formData.firm || 'Apicella',
        file_name: formData.file_name || '',
        town: formData.town || '',
        state: formData.state || '',
        date_of_loss: formData.date_of_loss || '',
        date_of_assignment: formData.date_of_assignment || '',
        policy_number: formData.policy_number || '',
        claim_number: formData.claim_number || '',
        our_estimate_value: parseFloat(formData.our_estimate_value) || 0,
        expected_loss_settlement: parseFloat(formData.expected_loss_settlement) || 0,
        estimate_fee: parseFloat(formData.estimate_fee) || 0,
        fee_percentage: formData.fee_percentage ? parseFloat(formData.fee_percentage) : null,
        actual_loss_settlement: parseFloat(formData.actual_loss_settlement) || 0,
        offer_received: formData.offer_received,
        cat_event_id: formData.cat_event_id || null
      };
      
      await axios.post(`${API}/claims`, payload);
      
      toast.success('Claim created successfully');
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create claim');
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      customer_name: '',
      assigned_to: '',
      firm: 'Apicella',
      file_name: '',
      town: '',
      state: '',
      date_of_loss: '',
      date_of_assignment: '',
      policy_number: '',
      claim_number: '',
      our_estimate_value: '',
      expected_loss_settlement: '',
      estimate_fee: '',
      fee_percentage: '',
      actual_loss_settlement: '',
      offer_received: false,
      status: 'Open',
      internal_comments: '',
      notes: '',
      cat_event_id: ''
    });
  };

  // Handle customer selection - auto-fill customer name
  const handleCustomerSelect = (customerId) => {
    if (customerId === 'none' || !customerId) {
      setFormData({...formData, customer_id: '', customer_name: ''});
    } else {
      const customer = customers.find(c => c.customer_id === customerId);
      setFormData({
        ...formData, 
        customer_id: customerId, 
        customer_name: customer?.name || ''
      });
    }
  };

  // Auto-calculate expected loss settlement when our estimate value changes
  const handleEstimateValueChange = (value) => {
    const estimateValue = parseFloat(value) || 0;
    const expectedSettlement = estimateValue * 0.75;
    
    setFormData(prev => {
      const newData = {
        ...prev,
        our_estimate_value: value,
        expected_loss_settlement: expectedSettlement.toFixed(2)
      };
      
      // Recalculate fee if percentage exists
      if (prev.fee_percentage) {
        const feePercentage = parseFloat(prev.fee_percentage);
        const baseAmount = parseFloat(prev.actual_loss_settlement) || expectedSettlement;
        newData.estimate_fee = (baseAmount * (feePercentage / 100)).toFixed(2);
      }
      
      return newData;
    });
  };

  // Handle manual expected loss settlement change
  const handleExpectedSettlementChange = (value) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        expected_loss_settlement: value
      };
      
      // Recalculate fee if percentage exists and no actual settlement
      if (prev.fee_percentage && !prev.actual_loss_settlement) {
        const feePercentage = parseFloat(prev.fee_percentage);
        const expectedSettlement = parseFloat(value) || 0;
        newData.estimate_fee = (expectedSettlement * (feePercentage / 100)).toFixed(2);
      }
      
      return newData;
    });
  };

  // Auto-calculate estimate fee when fee percentage changes
  const handleFeePercentageChange = (value) => {
    const feePercentage = parseFloat(value) || 0;
    
    // Use actual loss settlement if available, otherwise use expected
    const actualSettlement = parseFloat(formData.actual_loss_settlement);
    const expectedSettlement = parseFloat(formData.expected_loss_settlement);
    const baseAmount = actualSettlement || expectedSettlement || 0;
    
    const calculatedFee = baseAmount * (feePercentage / 100);
    
    setFormData(prev => ({
      ...prev,
      fee_percentage: value,
      estimate_fee: calculatedFee.toFixed(2)
    }));
  };

  // Recalculate fee when actual loss settlement changes
  const handleActualSettlementChange = (value) => {
    setFormData(prev => ({
      ...prev,
      actual_loss_settlement: value
    }));
    
    // If fee percentage exists, recalculate fee based on actual settlement
    if (formData.fee_percentage) {
      const feePercentage = parseFloat(formData.fee_percentage);
      const actualSettlement = parseFloat(value) || 0;
      const calculatedFee = actualSettlement * (feePercentage / 100);
      
      setFormData(prev => ({
        ...prev,
        estimate_fee: calculatedFee.toFixed(2)
      }));
    }
  };

  const handleExportClaims = async () => {
    try {
      const response = await axios.get(`${API}/export/claims?format=xlsx`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'claims.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Claims exported successfully');
    } catch (error) {
      toast.error('Failed to export claims');
    }
  };

  const handleReserveReportExport = async () => {
    if (selectedClaims.length === 0) {
      toast.error('Please select at least one claim');
      return;
    }
    if (selectedFields.length === 0) {
      toast.error('Please select at least one field');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/export/reserve-report`,
        { claim_ids: selectedClaims, fields: selectedFields },
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'reserve_report.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Reserve Report exported successfully');
      setIsReserveReportDialogOpen(false);
      setSelectedClaims([]);
    } catch (error) {
      toast.error('Failed to export reserve report');
    }
  };

  const handleClaimSelection = (claimId, checked) => {
    if (checked) {
      setSelectedClaims(prev => [...prev, claimId]);
    } else {
      setSelectedClaims(prev => prev.filter(id => id !== claimId));
    }
  };

  const handleSelectAllClaims = (checked) => {
    if (checked) {
      const filteredClaimIds = getFilteredClaims().map(c => c.claim_id);
      setSelectedClaims(filteredClaimIds);
    } else {
      setSelectedClaims([]);
    }
  };

  const handleFieldToggle = (fieldKey, checked) => {
    if (checked) {
      setSelectedFields(prev => [...prev, fieldKey]);
    } else {
      setSelectedFields(prev => prev.filter(f => f !== fieldKey));
    }
  };

  // Helper to check if user has access to all firms
  const hasAllFirmAccess = () => {
    const firmAccess = user?.firm_access;
    return !firmAccess || (firmAccess.includes('Apicella') && firmAccess.includes('MHA'));
  };

  // Get filtered claims based on all filters
  const getFilteredClaims = () => {
    const filtered = claims.filter(claim => {
      const adjusterMatch = adjusterFilter === 'all' || claim.assigned_to === adjusterFilter;
      const firmMatch = firmFilter === 'all' || claim.firm === firmFilter;
      return adjusterMatch && firmMatch;
    });
    
    // Apply sorting
    if (sortBy === 'firm-asc') {
      filtered.sort((a, b) => (a.firm || '').localeCompare(b.firm || ''));
    } else if (sortBy === 'firm-desc') {
      filtered.sort((a, b) => (b.firm || '').localeCompare(a.firm || ''));
    }
    
    return filtered;
  };

  const handleFirmSort = () => {
    if (sortBy === null || sortBy === 'firm-desc') {
      setSortBy('firm-asc');
    } else {
      setSortBy('firm-desc');
    }
  };

  const canAddClaim = hasRole(['admin', 'manager', 'staff']);

  return (
    <DashboardLayout>
      <div className="p-8" data-testid="claims-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold font-sans text-foreground tracking-tight">Claims</h1>
            <p className="text-foreground-muted mt-2">Manage claims and track settlements</p>
          </div>
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              onClick={() => setIsReserveReportDialogOpen(true)}
              data-testid="reserve-report-button"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Reserve Report
            </Button>
            <Button 
              variant="outline" 
              onClick={handleExportClaims}
              data-testid="export-claims-button"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
            {canAddClaim && (
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-accent hover:bg-accent-hover text-accent-foreground" data-testid="add-claim-button">
                    <Plus className="w-4 h-4 mr-2" />
                    New Claim
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-sans">Create New Claim</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="claim-form">
                    <div className="space-y-2">
                      <Label htmlFor="customer_id">Customer</Label>
                      <Select 
                        value={formData.customer_id} 
                        onValueChange={handleCustomerSelect}
                      >
                        <SelectTrigger data-testid="customer-select" className="bg-white">
                          <SelectValue placeholder="Select customer (optional)" />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="none">No Customer</SelectItem>
                          {customers.map((customer) => (
                            <SelectItem key={customer.customer_id} value={customer.customer_id}>
                              {customer.name} - {customer.email}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="customer_name">Customer Name</Label>
                      <Input
                        id="customer_name"
                        value={formData.customer_name}
                        onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                        placeholder="Enter customer name"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="assigned_to">Assigned To <span className="text-red-500">*</span></Label>
                      <Select 
                        value={formData.assigned_to} 
                        onValueChange={(value) => setFormData({...formData, assigned_to: value})}
                      >
                        <SelectTrigger data-testid="user-select" className="bg-white">
                          <SelectValue placeholder="Select user" />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          {users.map((user) => (
                            <SelectItem key={user.user_id} value={user.user_id}>
                              {user.name} ({user.role})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="firm">Firm <span className="text-red-500">*</span></Label>
                      <Select 
                        value={formData.firm} 
                        onValueChange={(value) => setFormData({...formData, firm: value})}
                      >
                        <SelectTrigger data-testid="firm-select" className="bg-white">
                          <SelectValue placeholder="Select firm" />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="Apicella">Apicella</SelectItem>
                          <SelectItem value="MHA">MHA</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Catastrophe Event</Label>
                      <CatEventSelect
                        catEvents={catEvents.filter(e => e.status === 'Open' || e.cat_event_id === formData.cat_event_id)}
                        value={formData.cat_event_id}
                        onChange={(value) => setFormData({...formData, cat_event_id: value})}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="file_name">File Name</Label>
                      <Input
                        id="file_name"
                        value={formData.file_name}
                        onChange={(e) => setFormData({...formData, file_name: e.target.value})}
                        placeholder="Enter file name"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="town">Town</Label>
                        <Input
                          id="town"
                          value={formData.town}
                          onChange={(e) => setFormData({...formData, town: e.target.value})}
                          placeholder="City/Town"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="state">State</Label>
                        <Input
                          id="state"
                          value={formData.state}
                          onChange={(e) => setFormData({...formData, state: e.target.value})}
                          placeholder="State"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="date_of_loss">Date of Loss</Label>
                        <Input
                          id="date_of_loss"
                          type="date"
                          value={formData.date_of_loss}
                          onChange={(e) => setFormData({...formData, date_of_loss: e.target.value})}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="date_of_assignment">Date of Assignment</Label>
                        <Input
                          id="date_of_assignment"
                          type="date"
                          value={formData.date_of_assignment}
                          onChange={(e) => setFormData({...formData, date_of_assignment: e.target.value})}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="policy_number">Policy Number</Label>
                        <Input
                          id="policy_number"
                          value={formData.policy_number}
                          onChange={(e) => setFormData({...formData, policy_number: e.target.value})}
                          placeholder="Policy #"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="claim_number">Claim Number</Label>
                        <Input
                          id="claim_number"
                          value={formData.claim_number}
                          onChange={(e) => setFormData({...formData, claim_number: e.target.value})}
                          placeholder="Claim #"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="our_estimate_value">Our Estimate Value ($) <span className="text-red-500">*</span></Label>
                        <Input
                          id="our_estimate_value"
                          type="number"
                          step="0.01"
                          value={formData.our_estimate_value}
                          onChange={(e) => handleEstimateValueChange(e.target.value)}
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="expected_loss_settlement">Expected Loss Settlement ($)</Label>
                        <Input
                          id="expected_loss_settlement"
                          type="number"
                          step="0.01"
                          value={formData.expected_loss_settlement}
                          onChange={(e) => handleExpectedSettlementChange(e.target.value)}
                          placeholder="Auto-calculated or enter manually"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="fee_percentage">Fee Percentage (%) <span className="text-red-500">*</span></Label>
                        <Input
                          id="fee_percentage"
                          type="number"
                          step="0.01"
                          value={formData.fee_percentage}
                          onChange={(e) => handleFeePercentageChange(e.target.value)}
                          required
                          placeholder="Enter percentage"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="estimate_fee">Estimate Fee ($)</Label>
                        <Input
                          id="estimate_fee"
                          type="number"
                          step="0.01"
                          value={formData.estimate_fee}
                          readOnly
                          className="bg-slate-100"
                          placeholder="Auto-calculated"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="actual_loss_settlement">Actual Loss Settlement ($)</Label>
                        <Input
                          id="actual_loss_settlement"
                          type="number"
                          step="0.01"
                          value={formData.actual_loss_settlement}
                          onChange={(e) => handleActualSettlementChange(e.target.value)}
                          placeholder="Enter actual amount"
                        />
                        <p className="text-xs text-foreground-muted">
                          If entered, fee will be calculated based on this amount instead of expected settlement
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="status">Status <span className="text-red-500">*</span></Label>
                        <Select 
                          value={formData.status} 
                          onValueChange={(value) => setFormData({...formData, status: value})}
                        >
                          <SelectTrigger className="bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white">
                            <SelectItem value="Open">Open</SelectItem>
                            <SelectItem value="Pending Docs">Pending Docs</SelectItem>
                            <SelectItem value="Negotiation">Negotiation</SelectItem>
                            <SelectItem value="Settled">Settled</SelectItem>
                            <SelectItem value="Paid in Full">Paid in Full</SelectItem>
                            <SelectItem value="Closed">Closed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="offer_received"
                        checked={formData.offer_received}
                        onChange={(e) => setFormData({...formData, offer_received: e.target.checked})}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      <Label htmlFor="offer_received" className="cursor-pointer">Offer Received</Label>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="internal_comments">Internal Comments</Label>
                      <Textarea
                        id="internal_comments"
                        value={formData.internal_comments}
                        onChange={(e) => setFormData({...formData, internal_comments: e.target.value})}
                        placeholder="Internal notes..."
                        rows={3}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="notes">Notes</Label>
                      <Textarea
                        id="notes"
                        value={formData.notes}
                        onChange={(e) => setFormData({...formData, notes: e.target.value})}
                        placeholder="Additional notes..."
                        rows={3}
                      />
                    </div>

                    <div className="flex justify-end gap-4">
                      <Button type="button" variant="secondary" onClick={() => setIsDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-primary hover:bg-primary-hover" data-testid="submit-claim">
                        Create Claim
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-foreground-muted" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Open">Open</SelectItem>
                <SelectItem value="Pending Docs">Pending Docs</SelectItem>
                <SelectItem value="Negotiation">Negotiation</SelectItem>
                <SelectItem value="Settled">Settled</SelectItem>
                <SelectItem value="Paid in Full">Paid in Full</SelectItem>
                <SelectItem value="Closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Adjuster Filter */}
          <div className="flex items-center gap-2">
            <Select value={adjusterFilter} onValueChange={setAdjusterFilter}>
              <SelectTrigger className="w-[200px]" data-testid="adjuster-filter">
                <SelectValue placeholder="Filter by adjuster" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Adjusters</SelectItem>
                {users.map((user) => {
                  const color = getUserColor(user.user_id, userColorMap);
                  return (
                    <SelectItem key={user.user_id} value={user.user_id}>
                      <div className="flex items-center gap-2">
                        <span className={`w-3 h-3 rounded-full ${color.bg} ${color.border} border`}></span>
                        {user.name}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Firm Filter - Only show for users with access to all firms */}
          {hasAllFirmAccess() && (
            <div className="flex items-center gap-2">
              <Select value={firmFilter} onValueChange={setFirmFilter}>
                <SelectTrigger className="w-[150px]" data-testid="firm-filter">
                  <SelectValue placeholder="Filter by firm" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Firms</SelectItem>
                  <SelectItem value="Apicella">Apicella</SelectItem>
                  <SelectItem value="MHA">MHA</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* CAT Event Filter */}
          <div className="flex items-center gap-2">
            <Select value={catEventFilter} onValueChange={setCatEventFilter}>
              <SelectTrigger className="w-[220px]" data-testid="cat-event-filter">
                <SelectValue placeholder="Filter by CAT event" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All CAT Events</SelectItem>
                <SelectItem value="none">No CAT Event</SelectItem>
                {catEvents.map((event) => (
                  <SelectItem key={event.cat_event_id} value={event.cat_event_id}>
                    {event.cat_code} — {event.event_type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Archive Toggle */}
          {hasRole(['admin']) && (
            <Button
              variant={showArchived ? "default" : "outline"}
              size="sm"
              onClick={() => setShowArchived(!showArchived)}
              data-testid="archive-toggle"
              className={showArchived ? "bg-amber-600 hover:bg-amber-700" : ""}
            >
              <Archive className="w-4 h-4 mr-2" />
              {showArchived ? "Viewing Archived" : "View Archived"}
            </Button>
          )}
        </div>

        {/* Claims Table */}
        <Card className="border border-border shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12" data-testid="loading-spinner">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
            </div>
          ) : claims.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="claims-table">
                <thead className="bg-slate-50 text-slate-500 font-medium uppercase tracking-wider text-xs border-b border-border">
                  <tr>
                    <th className="px-6 py-4 text-left">Claim ID</th>
                    <th className="px-6 py-4 text-left">Customer</th>
                    <th className="px-6 py-4 text-left">Assigned To</th>
                    <th 
                      className="px-6 py-4 text-left cursor-pointer hover:bg-slate-100 select-none"
                      onClick={handleFirmSort}
                      data-testid="sort-by-firm"
                    >
                      <div className="flex items-center gap-1">
                        Firm
                        {sortBy === 'firm-asc' && <span className="text-accent">↑</span>}
                        {sortBy === 'firm-desc' && <span className="text-accent">↓</span>}
                        {!sortBy && <span className="text-slate-300">↕</span>}
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left">Status</th>
                    <th className="px-6 py-4 text-right">Expected Settlement</th>
                    <th className="px-6 py-4 text-right bg-amber-50 text-amber-800">Actual Settlement</th>
                    <th className="px-6 py-4 text-right">Estimate Fee</th>
                    <th className="px-6 py-4 text-right bg-emerald-50 text-emerald-800">Actual Fee</th>
                    <th className="px-6 py-4 text-right">Fee Balance</th>
                    <th className="px-6 py-4 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {getFilteredClaims().map((claim) => {
                    const userColor = getUserColor(claim.assigned_to, userColorMap);
                    return (
                    <tr 
                      key={claim.claim_id} 
                      className="hover:bg-slate-50/50 transition-colors"
                      data-testid={`claim-row-${claim.claim_id}`}
                    >
                      <td className="px-6 py-4 font-mono text-xs text-foreground font-medium">
                        {claim.claim_id}
                      </td>
                      <td className="px-6 py-4 text-foreground">
                        {claim.customer_name}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${userColor.bg} ${userColor.text} border ${userColor.border}`}>
                          <span className={`w-2 h-2 rounded-full ${userColor.text.replace('text-', 'bg-')}`}></span>
                          {claim.assigned_to_name}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getFirmBadgeClass(claim.firm)}`}>
                          {claim.firm || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[claim.status] || 'bg-gray-100 text-gray-700'}`}>
                          {claim.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right font-mono text-xs text-foreground">
                        ${claim.expected_loss_settlement.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-6 py-4 text-right font-mono text-xs bg-amber-50">
                        {claim.actual_loss_settlement > 0 ? (
                          <span className="font-semibold text-amber-700">
                            ${claim.actual_loss_settlement.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right font-mono text-xs text-foreground">
                        ${claim.estimate_fee.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-6 py-4 text-right font-mono text-xs bg-emerald-50">
                        {claim.actual_loss_settlement > 0 && claim.fee_percentage ? (
                          <span className="font-semibold text-emerald-700">
                            ${((claim.actual_loss_settlement * (claim.fee_percentage / 100)) || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right font-mono text-xs">
                        <span className={claim.fee_balance > 0 ? 'text-destructive font-semibold' : 'text-success-text'}>
                          ${claim.fee_balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/claims/${claim.claim_id}`)}
                            data-testid={`view-claim-${claim.claim_id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          {hasRole(['admin']) && !showArchived && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleArchive(claim.claim_id)}
                              data-testid={`archive-claim-${claim.claim_id}`}
                              className="text-amber-600 hover:text-amber-700"
                            >
                              <Archive className="w-4 h-4" />
                            </Button>
                          )}
                          {hasRole(['admin']) && showArchived && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleUnarchive(claim.claim_id)}
                              data-testid={`unarchive-claim-${claim.claim_id}`}
                              className="text-green-600 hover:text-green-700"
                            >
                              <ArchiveRestore className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12" data-testid="no-claims">
              <p className="text-foreground-muted">{showArchived ? 'No archived claims' : 'No claims found'}</p>
              {canAddClaim && !showArchived && (
                <Button 
                  className="mt-4" 
                  variant="outline"
                  onClick={() => setIsDialogOpen(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Claim
                </Button>
              )}
            </div>
          )}
        </Card>

        {/* Reserve Report Dialog */}
        <Dialog open={isReserveReportDialogOpen} onOpenChange={setIsReserveReportDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-sans">Export Reserve Report</DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              {/* Field Selection */}
              <div>
                <Label className="text-sm font-medium mb-3 block">Select Fields to Include</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 p-4 border rounded-md bg-slate-50">
                  {RESERVE_REPORT_FIELDS.map((field) => (
                    <div key={field.key} className="flex items-center gap-2">
                      <Checkbox 
                        id={`field-${field.key}`}
                        checked={selectedFields.includes(field.key)}
                        onCheckedChange={(checked) => handleFieldToggle(field.key, checked)}
                      />
                      <Label htmlFor={`field-${field.key}`} className="text-sm cursor-pointer">
                        {field.label}
                      </Label>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-foreground-muted mt-2">{selectedFields.length} fields selected</p>
              </div>

              {/* Claim Selection */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-sm font-medium">Select Claims</Label>
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="select-all"
                      checked={selectedClaims.length === getFilteredClaims().length && getFilteredClaims().length > 0}
                      onCheckedChange={handleSelectAllClaims}
                    />
                    <Label htmlFor="select-all" className="text-sm cursor-pointer">
                      Select All ({getFilteredClaims().length})
                    </Label>
                  </div>
                </div>
                
                <div className="max-h-[300px] overflow-y-auto border rounded-md">
                  {getFilteredClaims().map((claim) => (
                    <div 
                      key={claim.claim_id}
                      className="flex items-center gap-3 p-3 border-b last:border-b-0 hover:bg-slate-50"
                    >
                      <Checkbox 
                        id={`claim-${claim.claim_id}`}
                        checked={selectedClaims.includes(claim.claim_id)}
                        onCheckedChange={(checked) => handleClaimSelection(claim.claim_id, checked)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs font-medium">{claim.claim_id}</span>
                          <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600">
                            {claim.firm || 'No Firm'}
                          </span>
                        </div>
                        <p className="text-sm text-foreground truncate">{claim.customer_name}</p>
                      </div>
                      <div className="text-right text-sm">
                        <p className="font-mono">${claim.expected_loss_settlement?.toLocaleString() || '0'}</p>
                        <p className="text-xs text-foreground-muted">{claim.town}, {claim.state}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-foreground-muted mt-2">{selectedClaims.length} claims selected</p>
              </div>

              <div className="flex justify-end gap-4 pt-4 border-t">
                <Button 
                  variant="secondary" 
                  onClick={() => {
                    setIsReserveReportDialogOpen(false);
                    setSelectedClaims([]);
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleReserveReportExport}
                  disabled={selectedClaims.length === 0}
                  className="bg-primary hover:bg-primary-hover"
                  data-testid="export-reserve-report"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export ({selectedClaims.length})
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};
